import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    computed,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
    DialogComponent,
    DialogDescriptionComponent,
    DialogSearchComponent,
    LoadMoreComponent,
    ScrollTrackerDirective,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { PaginatedList, isPaginatedList } from '@interacta-shared/util';
import { focusFirstNewChild } from '@interacta-shared/util-common';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import {
    CommunityPickerItem,
    WorkspacePickerItem,
} from '../../models/community-picker-item.model';

@Component({
    selector: 'interacta-community-picker-dialog',
    templateUrl: './community-picker-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogComponent,
        DialogDescriptionComponent,
        DialogSearchComponent,
        IconComponent,
        LoadMoreComponent,
        ScrollTrackerDirective,
        SeparatorComponent,
        TranslatePipe,
    ],
})
export class CommunityPickerDialogComponent {
    readonly workspaces = input.required<
        WorkspacePickerItem[] | PaginatedList<WorkspacePickerItem>
    >();
    readonly caption = input<string | null>(null);
    readonly isOpen = input(false);
    readonly recentCommunities = input<CommunityPickerItem[]>();
    readonly emptyLabel = input('UI.NO_RESULT');
    readonly nameFilterControl = input<FormControl<string>>();

    readonly closeDialog = output();
    readonly communitySelected = output<CommunityPickerItem>();
    readonly nextPage = output<void>();

    @ViewChild('workspaceListEl') workspaceListEl?: ElementRef<HTMLElement>;

    readonly isPaged = computed(() =>
        isPaginatedList<WorkspacePickerItem>(this.workspaces() as object),
    );

    readonly workspaceList = computed(() =>
        this.isPaged()
            ? (this.workspaces() as PaginatedList<WorkspacePickerItem>).list
            : (this.workspaces() as WorkspacePickerItem[]),
    );

    readonly isFetching = computed(() =>
        this.isPaged()
            ? (this.workspaces() as PaginatedList<WorkspacePickerItem>)
                  .isFetching
            : false,
    );

    readonly hasNextPage = computed(
        () =>
            this.isPaged() &&
            (this.workspaces() as PaginatedList<WorkspacePickerItem>)
                .nextPageToken.tag === 'regularLoading',
    );

    readonly communitiesTotalCount = computed(() =>
        this.workspaceList()
            .map((w) => w.communities.length)
            .reduce((a, b) => a + b, 0),
    );

    readonly accordionStates = signal<Record<number, { isOpen: boolean }>>({});

    private pendingObserver: MutationObserver | null = null;

    constructor() {
        toObservable(this.isOpen)
            .pipe(
                filter((isOpen) => !isOpen),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.nameFilterControl()?.reset());

        toObservable(this.workspaceList)
            .pipe(takeUntilDestroyed())
            .subscribe((list) => {
                this.accordionStates.update((map) => {
                    const next = { ...map };
                    for (const w of list) {
                        if (!(w.id in next)) next[w.id] = { isOpen: true };
                    }
                    return next;
                });
            });
    }

    toggleWorkspace(workspaceId: number): void {
        this.accordionStates.update((map) => ({
            ...map,
            [workspaceId]: { isOpen: !map[workspaceId]?.isOpen },
        }));
    }

    onNextPage(): void {
        if (this.workspaceListEl) {
            this.pendingObserver = focusFirstNewChild({
                container: this.workspaceListEl.nativeElement,
                currentChildCount: this.workspaceList().length,
                pendingObserver: this.pendingObserver,
            });
        }
        this.nextPage.emit();
    }

    onScrolled(endReached: boolean): void {
        if (endReached && this.hasNextPage() && !this.isFetching()) {
            this.onNextPage();
        }
    }
}
